import { useEffect, useState } from "react";
import { Loader2, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errorMessage } from "@/lib/api";
import { money } from "@/lib/pricing";
import { Panel } from "@/pages/Billing";

const cell = "h-9 rounded-lg border border-slate-800 bg-slate-900/70 px-3 text-sm text-slate-100 outline-none transition-colors focus:border-sky-500/60";

export function packError(p) {
  if (!String(p.name || "").trim()) return "Name is required";
  const c = Number(p.credits);
  if (!Number.isInteger(c) || c < 1) return "Credits must be at least 1";
  const pr = Number(p.price);
  if (!Number.isFinite(pr) || pr < 0.5) return "Price must be at least $0.50";
  return "";
}

function PackRow({ pack, idx, onSaved }) {
  const [p, setP] = useState(pack);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const isNew = !pack.id;
  const sfx = isNew ? "new" : idx;
  const save = async () => {
    const err = packError(p);
    setError(err);
    if (err) return;
    setBusy(true);
    try {
      const body = { name: p.name.trim(), credits: Number(p.credits), price: Number(p.price), active: !!p.active };
      if (isNew) await api.post("/admin/packs", body); else await api.put(`/admin/packs/${pack.id}`, body);
      toast.success(isNew ? "Pack added" : "Pack saved");
      if (isNew) setP({ name: "", credits: "", price: "", active: true });
      onSaved();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const credits = Number(p.credits), price = Number(p.price);
  return (
    <tr data-testid={`pack-row-${sfx}`} className="border-t border-slate-800/70 align-top">
      <td className="px-6 py-3"><input data-testid={`pack-name-input-${sfx}`} value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} placeholder="e.g. Starter 5" className={`${cell} w-full`} /></td>
      <td className="px-3 py-3"><input data-testid={`pack-credits-input-${sfx}`} value={p.credits} onChange={(e) => setP({ ...p, credits: e.target.value })} inputMode="numeric" className={`${cell} w-24 text-right font-mono-num`} /></td>
      <td className="px-3 py-3">
        <div className="relative"><span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">$</span>
          <input data-testid={`pack-price-input-${sfx}`} value={p.price} onChange={(e) => setP({ ...p, price: e.target.value })} inputMode="decimal" className={`${cell} w-28 pl-6 text-right font-mono-num`} /></div>
      </td>
      <td className="px-3 py-3 pt-5 text-right font-mono-num text-xs text-slate-500">{credits >= 1 && price > 0 ? money(price / credits) : "—"}</td>
      <td className="px-3 py-3 pt-4"><Switch data-testid={`pack-active-switch-${sfx}`} checked={!!p.active} onCheckedChange={(v) => setP({ ...p, active: v })} /></td>
      <td className="px-6 py-3 text-right">
        <button data-testid={`pack-save-button-${sfx}`} onClick={save} disabled={busy}
          className="inline-flex h-9 items-center gap-2 rounded-full bg-sky-500 px-4 text-sm font-semibold text-slate-950 transition-colors hover:bg-sky-400 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isNew ? <Plus className="h-4 w-4" /> : <Save className="h-4 w-4" />} {isNew ? "Add" : "Save"}
        </button>
        {error && <p data-testid={`pack-error-${sfx}`} className="mt-2 text-left text-xs text-red-300">{error}</p>}
      </td>
    </tr>
  );
}

function StarterCredits() {
  const [plan, setPlan] = useState("");
  const [error, setError] = useState("");
  useEffect(() => { api.get("/admin/settings").then((r) => setPlan(String(r.data.plan_credits))); }, []);
  const save = async (e) => {
    e.preventDefault();
    const p = Number(plan);
    if (!Number.isInteger(p) || p < 1 || p > 20) return setError("Plan reading credits must be a whole number from 1 to 20");
    setError("");
    try {
      await api.put("/admin/settings", { plan_credits: p });
      toast.success("Saved. New plan readings use this amount.");
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  return (
    <form onSubmit={save} className="flex flex-wrap items-center gap-3 p-6" data-testid="starter-credits-form">
      <input data-testid="plan-credits-input" value={plan} onChange={(e) => setPlan(e.target.value)} inputMode="numeric" className={`${cell} w-20 text-right font-mono-num`} />
      <span className="text-sm text-slate-400">credits per plans-only reading (schedules cost 1)</span>
      <button data-testid="starter-credits-save-button" className="inline-flex h-9 items-center gap-2 rounded-full bg-sky-500 px-4 text-sm font-semibold text-slate-950 hover:bg-sky-400"><Save className="h-4 w-4" /> Save</button>
      {error && <p data-testid="starter-credits-error" className="w-full text-xs text-red-300">{error}</p>}
    </form>
  );
}

export default function AdminPacks() {
  const [packs, setPacks] = useState(null);
  const load = () => api.get("/admin/packs").then((r) => setPacks(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-8" data-testid="admin-packs-page">
      <div className="rise">
        <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">Packs & settings</h1>
        <p className="mt-3 text-sm text-slate-400 md:text-base">Credit packs shown on the Viewlux Billing page (USD). Inactive packs are hidden.</p>
      </div>
      <Panel title="Credit packs" testId="admin-packs-panel">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px]" data-testid="admin-packs-table">
            <thead><tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <th className="px-6 py-3">Name</th><th className="px-3 py-3">Credits</th><th className="px-3 py-3">Price</th>
              <th className="px-3 py-3 text-right">Per credit</th><th className="px-3 py-3">Active</th><th className="px-6 py-3" /></tr></thead>
            <tbody>
              {packs?.map((p, i) => <PackRow key={`${p.id}-${p.price}-${p.credits}-${p.active}`} pack={p} idx={i} onSaved={load} />)}
              <PackRow key="new" pack={{ name: "", credits: "", price: "", active: true }} idx={-1} onSaved={load} />
            </tbody>
          </table>
        </div>
      </Panel>
      <Panel title="Credits per plan reading" testId="admin-starter-panel"><StarterCredits /></Panel>
    </div>
  );
}
